<template>
  <AppLayout>
    <div class="p-4 md:p-6">

      <!-- Header (desktop only) -->
      <div class="hidden md:flex items-center justify-between mb-2">
        <div>
          <h1 class="text-2xl font-bold text-slate-800 dark:text-white">Cities</h1>
          <p class="text-slate-500 dark:text-slate-400 text-sm mt-0.5">
            <span v-if="dataLoading" class="inline-block w-28 h-3 bg-slate-200 dark:bg-slate-700 rounded-full animate-pulse"></span>
            <span v-else>{{ filtered.length }} of {{ cities.length }} {{ cities.length !== 1 ? 'cities' : 'city' }}</span>
          </p>
        </div>
        <button @click="openAdd"
          class="flex items-center gap-2 bg-teal-500 hover:bg-teal-400 active:scale-95 text-white
                 text-sm font-semibold px-4 py-2.5 rounded-xl shadow-sm shadow-teal-500/30 transition">
          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>
          </svg>
          <span class="hidden sm:inline">Add City</span>
          <span class="sm:hidden">Add</span>
        </button>
      </div>

      <!-- ── MOBILE HEADER + SEARCH ── -->
      <div class="md:hidden -mx-4 -mt-4 mb-1">
        <!-- Teal gradient hero -->
        <div class="bg-gradient-to-br from-teal-600 via-teal-500 to-cyan-500 px-4 pt-3 pb-6">
          <div class="flex items-center justify-between mb-3">
            <div>
              <h1 class="text-xl font-bold text-white">Cities</h1>
              <p class="text-teal-200 text-xs mt-0.5">
                <span v-if="dataLoading">Loading...</span>
                <span v-else>{{ filtered.length}} of {{ cities.length }} {{ cities.length !== 1 ? 'cities' : 'city' }}</span>
              </p>
            </div>
            <button @click="openAdd"
              class="flex items-center gap-1.5 bg-white/20 active:bg-white/30 text-white text-sm font-semibold px-4 py-2.5 rounded-xl transition">
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>
              </svg>
              Add
            </button>
          </div>
        </div>

        <!-- Search card pulls up over hero -->
        <div class="bg-gray-50 dark:bg-slate-950 rounded-t-3xl -mt-4 px-4 pt-3 pb-2">
          <!-- Search input -->
          <div class="relative mb-2">
            <svg class="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none"
              fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607z"/>
            </svg>
            <input v-model="search" type="text" placeholder="Search cities..."
              class="w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                     text-slate-800 dark:text-white placeholder-slate-400 rounded-2xl pl-10 pr-10 py-3 text-sm
                     focus:outline-none focus:ring-2 focus:ring-teal-500 shadow-sm transition" />
            <button v-if="search" @click="search = ''"
              class="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400">
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>
      </div>

      <!-- Search (desktop only) -->
      <div class="hidden md:flex items-center gap-3 mb-3">
        <!-- Search -->
        <div class="relative w-56">
          <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none"
            fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607z"/>
          </svg>
          <input v-model="search" type="text" placeholder="Search..."
            class="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700
                   text-slate-800 dark:text-white placeholder-slate-400 rounded-xl pl-9 pr-8 py-1.5 text-sm
                   focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition" />
          <button v-if="search" @click="search = ''"
            class="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-500 dark:text-slate-300 transition">
            <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <!-- Spacer -->
        <div class="flex-1"></div>
      </div>

      <!-- Flash -->
      <div v-if="$page.props.flash?.notice"
        class="mb-4 flex items-center gap-2 bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-400 text-sm px-4 py-3 rounded-xl">
        <svg class="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
        {{ $page.props.flash.notice }}
      </div>
      <div v-if="$page.props.flash?.alert || errors?.length"
        class="mb-4 bg-red-50 dark:bg-red-950 text-red-600 dark:text-red-400 text-sm px-4 py-3 rounded-xl">
        <p v-if="$page.props.flash?.alert">• {{ $page.props.flash.alert }}</p>
        <p v-for="e in (errors || [])" :key="e">• {{ e }}</p>
      </div>

      <!-- Skeleton -->
      <template v-if="dataLoading">
        <!-- Desktop skeleton -->
        <div class="hidden md:block bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 overflow-hidden">
          <div class="border-b border-slate-100 dark:border-slate-800 px-5 py-3.5 flex gap-8">
            <div v-for="i in 3" :key="i" class="h-2.5 w-20 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse"></div>
          </div>
          <div v-for="i in 7" :key="i" class="flex items-center gap-6 px-5 py-4 border-b border-slate-50 dark:border-slate-800/50">
            <div class="h-8 w-8 rounded-full bg-slate-100 dark:bg-slate-800 animate-pulse shrink-0"></div>
            <div class="flex-1 space-y-1.5">
              <div class="h-3 w-32 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse"></div>
            </div>
            <div class="h-7 w-14 bg-slate-100 dark:bg-slate-800 rounded-lg animate-pulse"></div>
          </div>
        </div>
        <!-- Mobile skeleton -->
        <div class="md:hidden space-y-2.5">
          <div v-for="i in 5" :key="i"
            class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 overflow-hidden">
            <div class="flex items-center gap-3 px-4 py-3.5">
              <div class="w-11 h-11 rounded-full bg-slate-100 dark:bg-slate-800 animate-pulse shrink-0"></div>
              <div class="flex-1 space-y-2">
                <div class="h-3 w-32 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse"></div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- Empty state (desktop only) -->
      <div v-else-if="filtered.length === 0" class="hidden md:flex flex-col items-center justify-center py-20 text-center">
        <div class="w-16 h-16 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4">
          <svg class="w-8 h-8 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21"/>
          </svg>
        </div>
        <p class="text-slate-600 dark:text-slate-400 font-medium">
          {{ search ? 'No cities match your search' : 'No cities yet' }}
        </p>
        <p class="text-slate-400 dark:text-slate-500 text-sm mt-1">
          {{ search ? 'Try a different keyword' : 'Add your first city to get started' }}
        </p>
        <button v-if="!search" @click="openAdd" class="mt-4 text-teal-500 text-sm font-semibold hover:text-teal-400 transition">+ Add City</button>
        <button v-else @click="search = ''" class="mt-4 text-teal-500 text-sm font-semibold hover:text-teal-400 transition">Clear search</button>
      </div>

      <!-- Desktop table & Mobile cards -->
      <template v-else>
        <!-- Desktop table -->
        <div class="hidden md:block bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 overflow-visible">
          <table class="w-full text-sm border-collapse" style="border-radius: inherit;">
            <thead>
              <tr class="border-b border-slate-100 dark:border-slate-800">
                <th class="text-left px-5 py-3.5 text-xs font-semibold text-slate-400 uppercase tracking-wide w-12">#</th>
                <th class="text-left px-5 py-3.5 text-xs font-semibold text-slate-400 uppercase tracking-wide">City Name</th>
                <th class="text-left px-5 py-3.5 text-xs font-semibold text-slate-400 uppercase tracking-wide w-32">Created</th>
                <th class="text-center px-5 py-3.5 text-xs font-semibold text-slate-400 uppercase tracking-wide w-28">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(city, i) in filtered" :key="city.id"
                class="border-b border-slate-50 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition">
                <td class="px-5 py-4 text-slate-400 text-xs">{{ i + 1 }}</td>
                <td class="px-5 py-4">
                  <div class="flex items-center gap-3">
                    <div class="w-8 h-8 rounded-full bg-teal-100 dark:bg-teal-900 flex items-center justify-center text-teal-600 dark:text-teal-400 text-sm font-bold shrink-0">
                      {{ city.name.charAt(0).toUpperCase() }}
                    </div>
                    <span class="font-semibold text-slate-800 dark:text-white">{{ city.name }}</span>
                  </div>
                </td>
                <td class="px-5 py-4 text-slate-500 dark:text-slate-400">{{ city.created_at }}</td>
                <td class="px-5 py-4 text-center" @click.stop>
                  <div class="relative inline-block">
                    <button @click.stop="toggleActionDropdown(city.id)"
                      class="inline-flex items-center justify-center w-8 h-8 text-slate-500 dark:text-slate-400
                             hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition">
                      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
                      </svg>
                    </button>
                    <!-- Dropdown menu -->
                    <div v-if="actionDropdownId === city.id" @click.stop
                      class="absolute right-0 bottom-full mb-1 w-40 bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 z-50 overflow-hidden">
                      <button @click="openEdit(city); actionDropdownId = null"
                        class="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-slate-700 dark:text-slate-300
                               hover:bg-slate-50 dark:hover:bg-slate-700 transition">
                        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                          <path stroke-linecap="round" stroke-linejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z"/>
                        </svg>
                        Edit
                      </button>
                      <button @click="openDeleteConfirm(city); actionDropdownId = null"
                        class="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 dark:text-red-400
                               hover:bg-red-50 dark:hover:bg-red-950 transition border-t border-slate-100 dark:border-slate-700">
                        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                          <path stroke-linecap="round" stroke-linejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"/>
                        </svg>
                        Delete
                      </button>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Mobile cards -->
        <div class="md:hidden space-y-2.5">
        <div v-for="city in filtered" :key="city.id"
          class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 overflow-hidden">
          <!-- Main row -->
          <div class="flex items-center gap-3 px-4 py-3.5">
            <div class="w-11 h-11 rounded-full bg-teal-100 dark:bg-teal-900 flex items-center justify-center text-teal-600 dark:text-teal-400 font-bold shrink-0">
              {{ city.name.charAt(0).toUpperCase() }}
            </div>
            <div class="flex-1 min-w-0">
              <div class="font-semibold text-slate-800 dark:text-white">{{ city.name }}</div>
              <p class="text-xs text-slate-400 mt-0.5">Created {{ city.created_at }}</p>
            </div>
          </div>

          <!-- Action row -->
          <div class="bg-white dark:bg-slate-900">
            <!-- Edit & Delete buttons -->
            <div class="grid grid-cols-2 gap-2 px-4 pt-3 pb-3 border-t border-slate-100 dark:border-slate-800">
              <button @click.stop="openEdit(city)"
                class="flex items-center justify-center gap-2 text-sm font-semibold text-teal-600 dark:text-teal-400
                       bg-white dark:bg-slate-900 border border-teal-200 dark:border-teal-800 rounded-xl py-2.5 transition active:scale-95">
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z"/>
                </svg>
                Edit
              </button>
              <button @click.stop="openDeleteConfirm(city)"
                class="flex items-center justify-center gap-2 text-sm font-semibold text-red-600 dark:text-red-400
                       bg-white dark:bg-slate-900 border border-red-200 dark:border-red-800 rounded-xl py-2.5 transition active:scale-95">
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"/>
                </svg>
                Delete
              </button>
            </div>
          </div>
        </div>

        <!-- Mobile empty state -->
        <div v-if="filtered.length === 0" class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-12 text-center">
          <div class="w-16 h-16 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4 mx-auto">
            <svg class="w-8 h-8 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21"/>
            </svg>
          </div>
          <p class="text-slate-600 dark:text-slate-400 font-medium">
            {{ search ? 'No cities found' : 'No cities yet' }}
          </p>
          <p class="text-slate-400 dark:text-slate-500 text-sm mt-1">
            {{ search ? 'Try a different keyword' : 'Add your first city to get started' }}
          </p>
        </div>
        </div>
      </template>

    </div>

    <!-- ── ADD / EDIT MODAL ── -->
    <Modal v-if="showModal" @close="closeModal">
      <template #title>{{ editTarget ? 'Edit City' : 'Add City' }}</template>
      <form @submit.prevent="submitForm" class="space-y-4">
        <InputField v-model="form.name" label="City Name" placeholder="Lahore" required />

        <div class="flex items-center gap-3 pt-2">
          <button type="button" @click="closeModal"
            class="flex-1 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 active:scale-95
                   text-slate-700 dark:text-slate-300 font-semibold px-4 py-3 rounded-xl transition">
            Cancel
          </button>
          <button type="submit"
            class="flex-1 bg-teal-500 hover:bg-teal-400 active:scale-95 text-white font-semibold px-4 py-3 rounded-xl transition shadow-sm shadow-teal-500/20">
            {{ editTarget ? 'Update City' : 'Add City' }}
          </button>
        </div>
      </form>
    </Modal>

    <!-- Delete Confirmation Modal (matching customers style) -->
    <Modal v-if="showDeleteModal" @close="showDeleteModal = false">
      <template #title>Delete City</template>
      <div class="space-y-4">
        <p class="text-sm text-slate-600 dark:text-slate-400">
          Are you sure you want to delete <span class="font-semibold text-slate-800 dark:text-white">{{ cityToDelete?.name }}</span>?
          This action cannot be undone.
        </p>

        <div class="flex items-center gap-3 pt-2">
          <button type="button" @click="showDeleteModal = false"
            class="flex-1 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 active:scale-95
                   text-slate-700 dark:text-slate-300 font-semibold px-4 py-3 rounded-xl transition">
            Cancel
          </button>
          <button type="button" @click="deleteCity"
            class="flex-1 bg-red-500 hover:bg-red-400 active:scale-95 text-white font-semibold px-4 py-3 rounded-xl transition shadow-sm shadow-red-500/20">
            Delete City
          </button>
        </div>
      </div>
    </Modal>

  </AppLayout>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { router, usePage } from '@inertiajs/vue3'
import AppLayout from '@/layouts/AppLayout.vue'
import Modal from '@/components/Modal.vue'
import InputField from '@/components/InputField.vue'

const page = usePage()

const cities = ref([])
const dataLoading = ref(true)
const showModal = ref(false)
const editTarget = ref(null)
const showDeleteModal = ref(false)
const cityToDelete = ref(null)
const search = ref('')
const actionDropdownId = ref(null)
const errors = computed(() => page.props.errors)

const form = ref({
  name: '',
})

const filtered = computed(() => {
  if (!search.value) return cities.value
  const q = search.value.toLowerCase()
  return cities.value.filter(c => c.name.toLowerCase().includes(q))
})

onMounted(() => {
  fetchData()
  // Close dropdowns when clicking outside
  document.addEventListener('click', () => { actionDropdownId.value = null })
})

function fetchData() {
  dataLoading.value = true
  fetch('/cities/data')
    .then(res => res.json())
    .then(data => {
      cities.value = data.cities
      dataLoading.value = false
    })
    .catch(err => {
      console.error('Error fetching cities:', err)
      dataLoading.value = false
    })
}

function toggleActionDropdown(id) {
  actionDropdownId.value = actionDropdownId.value === id ? null : id
}

function openAdd() {
  editTarget.value = null
  form.value = { name: '' }
  showModal.value = true
}

function openEdit(city) {
  editTarget.value = city
  form.value = { name: city.name }
  showModal.value = true
}

function closeModal() {
  showModal.value = false
  editTarget.value = null
  form.value = { name: '' }
}

function submitForm() {
  if (editTarget.value) {
    router.patch(`/cities/${editTarget.value.id}`, { city: form.value }, {
      onSuccess: () => {
        closeModal()
        fetchData()
      }
    })
  } else {
    router.post('/cities', { city: form.value }, {
      onSuccess: () => {
        closeModal()
        fetchData()
      }
    })
  }
}

function openDeleteConfirm(city) {
  cityToDelete.value = city
  showDeleteModal.value = true
}

function deleteCity() {
  router.delete(`/cities/${cityToDelete.value.id}`, {
    onSuccess: () => {
      showDeleteModal.value = false
      cityToDelete.value = null
      fetchData()
    }
  })
}
</script>
